import React from "react";
import type { BankAccessBody } from "./BankAccessDialog";
import { HttpStatus } from "../api/httpStatus";

/** API-Form von `BankingOperationStatus` (shared/backend/Banking/Online/BankingOperationRunner). */
export type BankingOperationStatusView<TResult> = {
  operationId: string;
  state: "Running" | "Completed" | "Failed" | "Cancelled";
  result?: TResult | null;
  errorKey?: string | null;
  errorStatus?: number | null;
};

export type BankingOperationTransport<TResult> = {
  /** Startet den Vorgang; der Server antwortet sofort (202). Ein Fehler hier heißt: nicht gestartet. */
  start: (body: BankAccessBody) => Promise<unknown>;
  /** Zustand des eigenen Vorgangs; null, wenn unbekannt oder abgelaufen. */
  status: (operationId: string) => Promise<BankingOperationStatusView<TResult> | null>;
  cancel: (operationId: string) => Promise<unknown>;
  /** Produktfehler eines fehlgeschlagenen Vorgangs, z. B. der API-Fehler mit Fehlerschlüssel. */
  toError: (status: BankingOperationStatusView<TResult>) => unknown;
  /**
   * Zähler der Echtzeit-Ereignisse „Bankvorgang geändert“ des Produkts. Jede Erhöhung fragt offene Vorgänge einmal ab;
   * es gibt kein Polling.
   */
  signal: number;
  connected?: boolean;
};

/**
 * Baut aus Start, Zustand und Echtzeit-Signal die `run`-Funktion des {@link BankAccessDialog}: Der Bankvorgang läuft im
 * Hintergrund des Servers, die Anfrage kehrt sofort zurück, und das Versprechen erfüllt sich erst mit dem Abschluss.
 * cancel wartet auf die Startbestätigung und den serverseitigen Abschluss des Abbruchs.
 * Ein bereits abgeschlossener Vorgang liefert sein tatsächliches Ergebnis zurück.
 */
export function useBankingOperationRun<TResult>({ start, status, cancel, toError, signal, connected = false }: BankingOperationTransport<TResult>) {
  const pending = React.useRef(new Map<string, { resolve: (value: TResult) => void; reject: (reason: unknown) => void; started: Promise<unknown> }>());

  const check = React.useCallback(async (operationId: string) => {
    const entry = pending.current.get(operationId);
    if (!entry) return;
    let current: BankingOperationStatusView<TResult> | null;
    try { await entry.started; current = await status(operationId); }
    catch (error) {
      const waiting = pending.current.get(operationId);
      const failure = typeof error === "object" && error !== null && "status" in error && error.status === HttpStatus.NotFound
        ? toError({ operationId, state: "Failed", errorKey: "banking.operationLost" }) : error;
      pending.current.delete(operationId); waiting?.reject(failure); return;
    }
    const waiting = pending.current.get(operationId);
    if (!waiting || current?.state === "Running") return;
    pending.current.delete(operationId);
    if (current?.state === "Completed") waiting.resolve(current.result as TResult);
    else waiting.reject(toError(current ?? { operationId, state: "Failed", errorKey: "banking.operationLost" }));
  }, [status, toError]);

  React.useEffect(() => {
    if (!signal && !connected) return;
    for (const operationId of pending.current.keys())
      void check(operationId);
  }, [signal, check, connected]);

  const run = React.useCallback((body: BankAccessBody, abort: AbortSignal) => new Promise<TResult>((resolve, reject) => {
    if (abort.aborted) { reject(new DOMException("aborted", "AbortError")); return; }
    const started = Promise.resolve().then(() => start(body));
    pending.current.set(body.operationId, { resolve, reject, started });
    abort.addEventListener("abort", () => {
      pending.current.delete(body.operationId);
      reject(new DOMException("aborted", "AbortError"));
    }, { once: true });
    // Ein Abschluss vor dem ersten Ereignis wird direkt nach dem Start erkannt.
    started.then(() => check(body.operationId)).catch((error) => {
      pending.current.delete(body.operationId);
      reject(error);
    });
  }), [start, check]);
  const cancelOperation = React.useCallback(async (operationId: string) => {
    const waiting = pending.current.get(operationId);
    if (waiting) await waiting.started;
    await cancel(operationId);
    const finished = await status(operationId);
    return finished?.state === "Completed" ? { completed: true, result: finished.result as TResult } : { completed: false };
  }, [cancel, status]);
  return { run, cancel: cancelOperation };
}
